import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import useInView from '../../useInView';
import { countries, photos } from '../../galleryData';
import {
  Main,
  MaxWidth,
  Header,
  HeaderLeft,
  Eyebrow,
  Heading,
  Lead,
  Stats,
  Stat,
  ViewAll,
  Rail,
  Card,
  CardImage,
  CardOverlay,
  CardTopRow,
  FlagBadge,
  DateBadge,
  CardBody,
  CardTitle,
  CardMeta,
  CardCta,
} from './TravelGallery.elements';

// Kart meta'sı tek satırda kalmalı — en fazla 2 şehir, kalanı "+N"
const cityLabel = (cities = []) =>
  cities.length <= 2
    ? cities.join(' · ')
    : `${cities.slice(0, 2).join(' · ')} +${cities.length - 2}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.08 * i, ease: 'easeOut' },
  }),
};

function TravelGallery() {
  const [railRef, inView] = useInView({ amount: 0.05 });

  const totalCountries = countries.length;
  const totalCities = countries.reduce((sum, c) => sum + c.cities.length, 0);
  const totalPhotos = photos.length;

  return (
    <Main id="gallery">
      <MaxWidth>
        <Header>
          <HeaderLeft>
            <Eyebrow variants={fade} initial="hidden" animate="visible">
              Off the keyboard
            </Eyebrow>
            <Heading variants={fade} custom={1} initial="hidden" animate="visible">
              Six months <span>in motion</span>
            </Heading>
            <Lead>
              When I'm not shipping code, I'm chasing light in new cities.
              A visual log of the places that shaped the first half of 2026 —
              {' '}{totalCities} cities across {totalCountries} countries.
            </Lead>
            <Stats>
              <Stat>
                <strong>{totalCountries}</strong>
                <span>Countries</span>
              </Stat>
              <Stat>
                <strong>{totalCities}</strong>
                <span>Cities</span>
              </Stat>
              <Stat>
                <strong>{totalPhotos}+</strong>
                <span>Photos</span>
              </Stat>
            </Stats>
          </HeaderLeft>

          <ViewAll to="/gallery">
            View full gallery <FiArrowRight />
          </ViewAll>
        </Header>

        <Rail ref={railRef}>
          {countries.map((c, i) => (
            <Card
              key={c.slug}
              to={`/gallery?c=${c.slug}`}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{
                duration: 0.5,
                delay: 0.06 * i,
                ease: 'easeOut',
              }}
            >
              <CardImage
                src={c.coverThumb || c.cover}
                srcSet={
                  c.coverThumb && c.cover
                    ? `${c.coverThumb} 600w, ${c.cover} 1200w`
                    : undefined
                }
                sizes="(max-width: 600px) 80vw, 320px"
                alt={`${c.name} cover`}
                loading="lazy"
              />
              <CardOverlay />
              <CardTopRow>
                <FlagBadge>
                  <span aria-hidden="true">{c.flag}</span>
                  {c.name}
                </FlagBadge>
                {c.period && <DateBadge>{c.period}</DateBadge>}
              </CardTopRow>
              <CardBody>
                <CardTitle>{c.name}</CardTitle>
                <CardMeta>
                  {cityLabel(c.cities)} · {c.photoCount} photos
                </CardMeta>
                <CardCta>
                  Explore <FiArrowRight />
                </CardCta>
              </CardBody>
            </Card>
          ))}
        </Rail>
      </MaxWidth>
    </Main>
  );
}

export default TravelGallery;
